import React, { useState, useEffect } from 'react';
import { ShieldAlert, ListChecks, MessageSquare, ArrowLeft, Layers3, QrCode, ChevronDown, ChevronUp, Users, Gamepad2 } from 'lucide-react';
import { User } from '../types';
import { isLocalBackend, phpApiUrl, phpAuthHeaders } from '../apiEnv';

import AdminSubmissions from './AdminSubmissions';
import AdminAnnouncement from './AdminAnnouncement';
import AdminTournamentMasterLists from './AdminTournamentMasterLists';
import AdminWeChatLiveCode from './AdminWeChatLiveCode';
import AdminUserManagement from './AdminUserManagement';
import AdminOnlineSettings from './AdminOnlineSettings';

interface AdminDashboardProps {
  user: User;
  onBack: () => void;
  onOpenChat: (uid: string) => void;
}

type AdminTab = 'feedback' | 'pointMasterLists' | 'drawMasterLists' | 'submissions' | 'announcement' | 'wechatLiveCode' | 'userManagement' | 'onlineSettings';
type FeedbackCategory = 'general' | 'mapSuggestions';

interface Feedback {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
}

interface MapSuggestion {
  id: string;
  userId: string;
  userDisplayName: string;
  address: string;
  name: string;
  group: 'practice' | 'club' | 'brand';
  createdAt: number;
  status: 'pending';
}

const MAP_SUGGESTION_GROUP_LABEL: Record<MapSuggestion['group'], string> = {
  practice: '团练地点',
  club: '俱乐部',
  brand: '剑玉品牌',
};

const ADMIN_TAB_META: Record<AdminTab, { label: string; activeClassName: string; inactiveClassName: string; icon: React.ComponentType<{ size?: number; className?: string }> }> = {
  feedback: {
    label: 'FEEDBACK / 反馈查看',
    activeClassName: 'bg-yellow-500 text-black shadow-[0_0_15px_rgba(234,179,8,0.4)]',
    inactiveClassName: 'text-zinc-400 hover:text-yellow-300 bg-zinc-900/40 md:bg-transparent',
    icon: MessageSquare,
  },
  pointMasterLists: {
    label: 'POINT LISTS / 积分总表',
    activeClassName: 'bg-cyan-500 text-black shadow-[0_0_15px_rgba(34,211,238,0.35)]',
    inactiveClassName: 'text-zinc-400 hover:text-cyan-300 bg-zinc-900/40 md:bg-transparent',
    icon: Layers3,
  },
  drawMasterLists: {
    label: 'DRAW LISTS / 抽招总表',
    activeClassName: 'bg-fuchsia-500 text-black shadow-[0_0_15px_rgba(217,70,239,0.35)]',
    inactiveClassName: 'text-zinc-400 hover:text-fuchsia-300 bg-zinc-900/40 md:bg-transparent',
    icon: Layers3,
  },
  submissions: {
    label: 'SUBMISSIONS / 招式提交',
    activeClassName: 'bg-blue-500 text-black shadow-[0_0_15px_rgba(59,130,246,0.4)]',
    inactiveClassName: 'text-zinc-400 hover:text-blue-300 bg-zinc-900/40 md:bg-transparent',
    icon: ListChecks,
  },
  announcement: {
    label: 'ANNOUNCEMENT / 首页公告',
    activeClassName: 'bg-amber-500 text-black shadow-[0_0_15px_rgba(245,158,11,0.35)]',
    inactiveClassName: 'text-zinc-400 hover:text-amber-300 bg-zinc-900/40 md:bg-transparent',
    icon: MessageSquare,
  },
  wechatLiveCode: {
    label: 'WECHAT QR / 微信活码',
    activeClassName: 'bg-emerald-500 text-black shadow-[0_0_15px_rgba(16,185,129,0.35)]',
    inactiveClassName: 'text-zinc-400 hover:text-emerald-300 bg-zinc-900/40 md:bg-transparent',
    icon: QrCode,
  },
  userManagement: {
    label: 'USERS / 用户管理',
    activeClassName: 'bg-orange-500 text-black shadow-[0_0_15px_rgba(249,115,22,0.4)]',
    inactiveClassName: 'text-zinc-400 hover:text-orange-300 bg-zinc-900/40 md:bg-transparent',
    icon: Users,
  },
  onlineSettings: {
    label: 'ONLINE / 联机设置',
    activeClassName: 'bg-blue-500 text-black shadow-[0_0_15px_rgba(59,130,246,0.4)]',
    inactiveClassName: 'text-zinc-400 hover:text-blue-300 bg-zinc-900/40 md:bg-transparent',
    icon: Gamepad2,
  },
};

const AdminDashboard: React.FC<AdminDashboardProps> = ({ user, onBack, onOpenChat }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('pointMasterLists');
  const [isMobileTabMenuOpen, setIsMobileTabMenuOpen] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState<FeedbackCategory>('general');
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [mapSuggestions, setMapSuggestions] = useState<MapSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isLocal = isLocalBackend();

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        isLocal ? '/api/admin/feedback' : phpApiUrl('admin_get_feedback'),
        { headers: phpAuthHeaders() }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setFeedbacks(data.feedback || []);
        setMapSuggestions(data.mapSuggestions || []);
      } else {
        setError(data.error || 'Failed to fetch feedback');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'feedback') {
      fetchFeedback();
    } else {
      setLoading(false);
      setError('');
    }
  }, [activeTab]);

  const activeTabMeta = ADMIN_TAB_META[activeTab];

  const handleTabChange = (tab: AdminTab) => {
    setActiveTab(tab);
    setIsMobileTabMenuOpen(false);
  };

  if (!user.isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full">
        <ShieldAlert className="text-red-500 w-24 h-24 mb-4" />
        <h2 className="text-2xl text-white font-orbitron">ACCESS DENIED / 拒绝访问</h2>
        <p className="text-zinc-500 mt-2">You do not have permission to view this page.</p>
        <button onClick={onBack} className="mt-8 px-6 py-2 bg-zinc-800 text-white rounded hover:bg-zinc-700 font-orbitron">
          RETURN
        </button>
      </div>
    );
  }

  return (
    <div className={`flex flex-col w-full min-w-0 relative ${activeTab === 'submissions' ? 'min-h-full' : 'h-full'}`}>
      <div className="absolute inset-0 bg-red-900/5 pointer-events-none"></div>

      <div className="flex flex-col gap-4 mb-4 md:mb-8 pb-4 border-b border-red-500/30 shrink-0">
        <div className="flex items-center gap-2 md:gap-4 shrink-0">
          <button onClick={onBack} className="p-1 md:p-2 text-zinc-400 hover:text-red-400 transition-colors rounded-lg hover:bg-red-500/10 shrink-0">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl md:text-3xl font-orbitron font-black text-red-500 tracking-widest uppercase neon-glow-red flex items-center gap-2 md:gap-3">
            <ShieldAlert className="w-6 h-6 md:w-8 md:h-8" />
            SUPER ADMIN
          </h1>
        </div>
        <div className="md:hidden rounded-lg border border-red-500/30 bg-black/50 p-1.5">
          <button
            type="button"
            onClick={() => setIsMobileTabMenuOpen(previous => !previous)}
            className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-3 text-left font-orbitron text-[11px] font-bold tracking-wider transition-all ${activeTabMeta.activeClassName}`}
          >
            <span className="flex min-w-0 items-center gap-2">
              <activeTabMeta.icon size={16} className="shrink-0" />
              <span className="truncate">{activeTabMeta.label}</span>
            </span>
            {isMobileTabMenuOpen ? <ChevronUp size={16} className="shrink-0" /> : <ChevronDown size={16} className="shrink-0" />}
          </button>
          {isMobileTabMenuOpen && (
            <div className="mt-1 grid grid-cols-1 gap-1">
              {(Object.entries(ADMIN_TAB_META) as Array<[AdminTab, typeof activeTabMeta]>)
                .filter(([tab]) => tab !== activeTab)
                .map(([tab, meta]) => {
                  const Icon = meta.icon;
                  return (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => handleTabChange(tab)}
                      className={`w-full min-w-0 rounded-md px-3 py-3 font-orbitron text-[11px] font-bold tracking-wider flex items-center justify-center gap-2 text-center leading-tight transition-all ${meta.inactiveClassName}`}
                    >
                      <Icon size={16} className="shrink-0" />
                      {meta.label}
                    </button>
                  );
                })}
            </div>
          )}
        </div>
        <div className="hidden md:grid md:grid-cols-2 xl:grid-cols-6 bg-black/50 border border-red-500/30 rounded-lg p-1 w-full shrink-0 gap-1">
          {(Object.entries(ADMIN_TAB_META) as Array<[AdminTab, typeof activeTabMeta]>).map(([tab, meta]) => {
            const Icon = meta.icon;
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`w-full min-w-0 px-3 py-2 md:px-4 lg:px-6 rounded-md font-orbitron text-[11px] md:text-xs lg:text-sm font-bold tracking-wider flex flex-col lg:flex-row items-center justify-center gap-1 lg:gap-2 text-center leading-tight transition-all ${
                  isActive ? meta.activeClassName : meta.inactiveClassName
                }`}
              >
                <Icon size={16} className="md:w-[18px] md:h-[18px]" />
                {meta.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className={activeTab === 'submissions'
        ? 'flex-1 min-w-0 overflow-x-hidden custom-scrollbar pr-0 md:pr-2'
        : 'flex-1 min-w-0 overflow-y-auto overflow-x-hidden min-h-0 custom-scrollbar pr-0 md:pr-2'
      }>
        {loading ? (
          <div className="text-center text-zinc-500 py-12 font-orbitron animate-pulse">LOADING DATA...</div>
        ) : error ? (
          <div className="text-center text-red-500 py-12 font-orbitron">{error}</div>
        ) : activeTab === 'submissions' ? (
          <AdminSubmissions user={user} />
        ) : activeTab === 'pointMasterLists' ? (
          <AdminTournamentMasterLists user={user} mode="points" />
        ) : activeTab === 'drawMasterLists' ? (
          <AdminTournamentMasterLists user={user} mode="draw" />
        ) : activeTab === 'announcement' ? (
          <AdminAnnouncement />
        ) : activeTab === 'wechatLiveCode' ? (
          <AdminWeChatLiveCode />
        ) : activeTab === 'userManagement' ? (
          <AdminUserManagement onOpenChat={onOpenChat} />
        ) : activeTab === 'onlineSettings' ? (
          <AdminOnlineSettings />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFeedbackCategory('general')}
                className={`rounded-xl border px-3 py-2 text-xs font-orbitron uppercase tracking-[0.14em] transition-colors ${
                  feedbackCategory === 'general'
                    ? 'border-yellow-400/50 bg-yellow-500/15 text-yellow-200'
                    : 'border-white/10 bg-black/35 text-zinc-400 hover:border-yellow-400/30 hover:text-yellow-200'
                }`}
              >
                普通反馈
              </button>
              <button
                onClick={() => setFeedbackCategory('mapSuggestions')}
                className={`rounded-xl border px-3 py-2 text-xs font-orbitron uppercase tracking-[0.14em] transition-colors ${
                  feedbackCategory === 'mapSuggestions'
                    ? 'border-cyan-400/50 bg-cyan-500/15 text-cyan-200'
                    : 'border-white/10 bg-black/35 text-zinc-400 hover:border-cyan-400/30 hover:text-cyan-200'
                }`}
              >
                地图标点
              </button>
            </div>
            {feedbackCategory === 'general' ? (
              feedbacks.length === 0 ? (
                <div className="text-center text-zinc-500 py-12 font-orbitron">No feedback found.</div>
              ) : (
                feedbacks.map((fb) => (
                  <div key={fb.id} className="bg-black/60 border border-yellow-500/20 rounded-xl p-5 hover:border-yellow-500/50 transition-colors">
                    <p className="text-white text-sm md:text-base mb-3 leading-relaxed">{fb.content}</p>
                    <div className="flex items-center justify-between text-xs text-zinc-600 font-mono border-t border-yellow-500/10 pt-3">
                      <span>User ID: {fb.user_id}</span>
                      <span>Date: {fb.created_at}</span>
                    </div>
                  </div>
                ))
              )
            ) : mapSuggestions.length === 0 ? (
              <div className="text-center text-zinc-500 py-12 font-orbitron">No map suggestions found.</div>
            ) : (
              mapSuggestions.map((suggestion) => (
                <div key={suggestion.id} className="bg-black/60 border border-cyan-500/20 rounded-xl p-5 hover:border-cyan-500/50 transition-colors">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-base font-orbitron text-cyan-100">{suggestion.name}</div>
                    <div className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2 py-1 text-[10px] font-orbitron uppercase tracking-[0.14em] text-cyan-200">
                      {MAP_SUGGESTION_GROUP_LABEL[suggestion.group] || suggestion.group}
                    </div>
                    <div className="rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-1 text-[10px] font-orbitron uppercase tracking-[0.14em] text-amber-200">
                      {suggestion.status}
                    </div>
                  </div>
                  <div className="mt-3 space-y-2 text-sm leading-relaxed text-zinc-300">
                    <div><span className="text-zinc-500">提交人：</span>{suggestion.userDisplayName} ({suggestion.userId})</div>
                    <div><span className="text-zinc-500">具体地址：</span>{suggestion.address}</div>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-zinc-600 font-mono border-t border-cyan-500/10 pt-3">
                    <span>Suggestion ID: {suggestion.id}</span>
                    <span>Date: {new Date(suggestion.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
